import { useEffect, useMemo, useState } from 'react'
import { Alert, Input, InputNumber, Modal, Radio, Select, Space, Switch } from 'antd'
import type { DataTypeName, RuleConfig, RuleEntry, RuleSeverity, RuleType } from '../../types/fileUploadRules'
import {
  DATA_TYPES,
  RULE_TYPES,
  RULE_TYPE_HINTS,
  SEVERITIES,
  entryKey,
  getRangeMode,
  validateEntry,
  type RangeMode,
} from './ruleConfig'

type Props = {
  open: boolean
  // The entry being edited, or null when adding a new one.
  entry: RuleEntry | null
  // Existing column+ruleType keys in the set, to block exact duplicates (excludes the one being edited).
  takenKeys: string[]
  onCancel: () => void
  onSave: (entry: RuleEntry) => void
}

function emptyEntry(): RuleEntry {
  return { columnName: '', ruleType: 'RequiredNonNull', severity: 'Error', isActive: true }
}

export default function RuleEntryModal({ open, entry, takenKeys, onCancel, onSave }: Props) {
  const [columnName, setColumnName] = useState('')
  const [ruleType, setRuleType] = useState<RuleType>('RequiredNonNull')
  const [severity, setSeverity] = useState<RuleSeverity>('Error')
  const [isActive, setIsActive] = useState(true)
  const [dataType, setDataType] = useState<DataTypeName>('date')
  const [rangeMode, setRangeMode] = useState<RangeMode>('numeric')
  const [min, setMin] = useState<number | null>(null)
  const [max, setMax] = useState<number | null>(null)
  const [minLength, setMinLength] = useState<number | null>(null)
  const [maxLength, setMaxLength] = useState<number | null>(null)
  const [pattern, setPattern] = useState('')
  const [ignoreCase, setIgnoreCase] = useState(false)
  const [values, setValues] = useState<string[]>([])
  const [caseInsensitive, setCaseInsensitive] = useState(false)
  const [removeNullRows, setRemoveNullRows] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    const source = entry ?? emptyEntry()
    const config = source.config ?? {}
    setColumnName(source.columnName)
    setRuleType(source.ruleType)
    setSeverity(source.severity)
    setIsActive(source.isActive)
    setDataType(config.type ?? 'date')
    setRangeMode(getRangeMode(config))
    setMin(config.min ?? null)
    setMax(config.max ?? null)
    setMinLength(config.minLength ?? null)
    setMaxLength(config.maxLength ?? null)
    setPattern(config.pattern ?? '')
    setIgnoreCase(config.ignoreCase ?? false)
    setValues(config.values ?? [])
    setCaseInsensitive(config.caseInsensitive ?? false)
    setRemoveNullRows(config.removeNullRows ?? false)
    setErrors([])
  }, [open, entry])

  const buildConfig = (): RuleConfig | undefined => {
    switch (ruleType) {
      case 'RequiredNonNull':
        return removeNullRows ? { removeNullRows: true } : undefined
      case 'DataType':
        return { type: dataType }
      case 'Range':
        return rangeMode === 'text'
          ? { minLength: minLength ?? undefined, maxLength: maxLength ?? undefined }
          : { min: min ?? undefined, max: max ?? undefined }
      case 'Regex':
        return { pattern, ignoreCase }
      case 'AllowedValues':
        return { values, caseInsensitive }
      default:
        return undefined
    }
  }

  const buildEntry = (): RuleEntry => ({
    columnName: columnName.trim(),
    ruleType,
    severity,
    isActive,
    config: buildConfig(),
  })

  const isDuplicate = useMemo(
    () => takenKeys.includes(entryKey(columnName, ruleType)),
    [takenKeys, columnName, ruleType],
  )

  const handleSave = () => {
    const next = buildEntry()
    const found = validateEntry(next, 0).map((message) => message.replace(/^Rule\[0\]:\s*/, ''))
    if (isDuplicate) found.push('A rule of this type already exists for this column.')
    if (found.length) {
      setErrors(found)
      return
    }
    onSave(next)
  }

  return (
    <Modal
      open={open}
      title={entry ? 'Edit rule' : 'Add rule'}
      okText="Save entry"
      onOk={handleSave}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Space direction="vertical" size={14} className="w-full">
        {errors.length > 0 && (
          <Alert
            type="error"
            showIcon
            message="Fix the following"
            description={
              <ul className="list-disc pl-4">
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            }
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column name</label>
            <Input value={columnName} onChange={(e) => setColumnName(e.target.value)} placeholder="e.g. DueDate" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <Select<RuleSeverity>
              className="w-full"
              value={severity}
              onChange={setSeverity}
              options={SEVERITIES.map((s) => ({ value: s, label: s }))}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rule type</label>
          <Select<RuleType>
            className="w-full"
            value={ruleType}
            onChange={setRuleType}
            options={RULE_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <p className="text-xs text-gray-400 mt-1">{RULE_TYPE_HINTS[ruleType]}</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-700 mb-2">Configuration</div>

          {ruleType === 'RequiredNonNull' && (
            <Space size={8}>
              <Switch checked={removeNullRows} onChange={setRemoveNullRows} size="small" />
              <span className="text-sm text-gray-700">Remove rows when this column is empty or NULL</span>
            </Space>
          )}

          {ruleType === 'DataType' && (
            <Select<DataTypeName>
              style={{ width: 220 }}
              value={dataType}
              onChange={setDataType}
              options={DATA_TYPES.map((t) => ({ value: t, label: t }))}
            />
          )}

          {ruleType === 'Range' && (
            <Space direction="vertical" size={10} className="w-full">
              <Radio.Group
                value={rangeMode}
                onChange={(e) => setRangeMode(e.target.value as RangeMode)}
                options={[
                  { value: 'numeric', label: 'Numeric range' },
                  { value: 'text', label: 'Text length' },
                ]}
                optionType="button"
              />
              {rangeMode === 'numeric' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min</label>
                    <InputNumber className="w-full" value={min} onChange={(v) => setMin(v ?? null)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max</label>
                    <InputNumber className="w-full" value={max} onChange={(v) => setMax(v ?? null)} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min length</label>
                    <InputNumber className="w-full" min={0} value={minLength} onChange={(v) => setMinLength(v ?? null)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max length</label>
                    <InputNumber className="w-full" min={0} value={maxLength} onChange={(v) => setMaxLength(v ?? null)} />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400">Only the selected pair is sent.</p>
            </Space>
          )}

          {ruleType === 'Regex' && (
            <Space direction="vertical" size={10} className="w-full">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Pattern</label>
                <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="^[A-Z]{3}$" />
              </div>
              <Space size={8}>
                <Switch checked={ignoreCase} onChange={setIgnoreCase} size="small" />
                <span className="text-sm text-gray-700">Ignore case</span>
              </Space>
            </Space>
          )}

          {ruleType === 'AllowedValues' && (
            <Space direction="vertical" size={10} className="w-full">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Allowed values</label>
                <Select
                  mode="tags"
                  className="w-full"
                  value={values}
                  onChange={setValues}
                  placeholder="Type a value and press Enter"
                  open={false}
                  suffixIcon={null}
                />
              </div>
              <Space size={8}>
                <Switch checked={caseInsensitive} onChange={setCaseInsensitive} size="small" />
                <span className="text-sm text-gray-700">Case insensitive</span>
              </Space>
            </Space>
          )}
        </div>

        <Space size={8}>
          <Switch checked={isActive} onChange={setIsActive} size="small" />
          <span className="text-sm text-gray-700">Entry active</span>
        </Space>
      </Space>
    </Modal>
  )
}
